import React, { useMemo } from 'react';
import { CardTitle, Link, LinkButton } from '@/shared/ui';
import { Award, Calendar, ExternalLink } from 'lucide-react';
import { Certification, formatMonthYear } from '@personal-portfolio/contract';
import { useSiteContent } from '@personal-portfolio/queries';

interface CertificationCardProps {
  certification: Certification;
}

export const CertificationCard: React.FC<CertificationCardProps> = ({
  certification,
}) => {
  const { cards } = useSiteContent();
  const certificationLink = useMemo(() => {
    return certification.links?.find((link) => link.type === 'certification');
  }, [certification]);

  const companyLink = useMemo(() => {
    return certification.links?.find(
      (link) => link.type === 'company-profile' || link.type === 'linkedin',
    );
  }, [certification]);

  const description = useMemo(() => {
    return certification.description || certification.summary;
  }, [certification]);

  return (
    <div
      id={certification.slug}
      className="group relative scroll-mt-20 overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-shadow duration-200 hover:shadow-md"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border bg-muted text-foreground">
            <Award size={24} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="mb-1 text-xl leading-tight font-semibold tracking-tight">
                  <CardTitle className="text-xl font-semibold tracking-tight">
                    {certificationLink?.url ? (
                      <Link
                        href={certificationLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="decoration-muted-foreground/40 underline-offset-4 hover:underline"
                      >
                        {certification.name}
                      </Link>
                    ) : (
                      certification.name
                    )}
                  </CardTitle>
                </h2>
                {companyLink?.url && (
                  <LinkButton
                    variant="link"
                    className="h-6 px-0 text-base font-medium text-muted-foreground"
                    href={companyLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {certification.issuer}
                  </LinkButton>
                )}
              </div>

              <div className="hidden items-center gap-1.5 rounded-md border bg-muted px-2.5 py-1.5 font-mono text-xs text-muted-foreground lg:flex">
                <Calendar size={14} />
                <span>{formatMonthYear(certification.issueDate)}</span>
              </div>
            </div>

            {description && (
              <div className="mb-3">
                <p className="text-sm leading-relaxed break-words text-muted-foreground">
                  {description}
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-xs">
              {certification.level && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">{cards.level}:</span>
                  <span className="font-medium text-foreground capitalize">
                    {certification.level.replace('-', ' ')}
                  </span>
                </div>
              )}
              {certification.credentialId && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">{cards.id}:</span>
                  <span className="rounded-md border bg-muted px-1.5 py-0.5 font-mono text-xs">
                    {certification.credentialId}
                  </span>
                </div>
              )}
              {certification.expiryDate && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">
                    {cards.expires}:
                  </span>
                  <span className="font-medium">
                    {formatMonthYear(certification.expiryDate)}
                  </span>
                </div>
              )}
            </div>

            {certificationLink && (
              <div className="flex flex-wrap gap-2 pt-4">
                <LinkButton
                  key={certificationLink.id}
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  href={certificationLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {certificationLink.label}
                  <ExternalLink className="ml-1" />
                </LinkButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
